import React, { useRef, useEffect, useState, useCallback } from 'react';

interface LineNumberedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  selectedLine?: number;
  onLineSelect?: (lineNumber: number) => void;
  className?: string;
  placeholder?: string;
}

export const LineNumberedTextarea: React.FC<LineNumberedTextareaProps> = ({
  value,
  onChange,
  selectedLine,
  onLineSelect,
  className = '',
  placeholder = '',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState(1);
  const [lineHeights, setLineHeights] = useState<number[]>([]);

  // Measure line heights
  const measureLineHeights = useCallback(() => {
    const lines = value.split('\n');
    setLineCount(lines.length || 1);

    // Measure the actual height of each line including wrapping
    if (measureRef.current && textareaRef.current) {
      const textarea = textareaRef.current;
      const measureDiv = measureRef.current;

      // Match textarea width for accurate wrapping
      measureDiv.style.width = `${textarea.clientWidth - 24}px`; // subtract padding

      const heights: number[] = [];
      lines.forEach((line, index) => {
        // Create a temporary element for each line
        const lineElement = document.createElement('div');
        lineElement.textContent = line || '\u00A0'; // Use non-breaking space for empty lines
        lineElement.style.whiteSpace = 'pre-wrap';
        lineElement.style.wordWrap = 'break-word';
        measureDiv.appendChild(lineElement);

        // Measure the height
        heights.push(lineElement.offsetHeight);

        // Clean up
        measureDiv.removeChild(lineElement);
      });

      setLineHeights(heights);
    }
  }, [value]);

  // Recalculate line heights when value changes
  useEffect(() => {
    measureLineHeights();
  }, [measureLineHeights]);

  // Recalculate line heights when textarea is resized
  useEffect(() => {
    if (!textareaRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      measureLineHeights();
    });

    resizeObserver.observe(textareaRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureLineHeights]);

  // No scroll synchronization needed - the entire component scrolls as one unit

  // Handle line selection when clicking on line numbers
  const handleLineNumberClick = (lineNumber: number) => {
    if (onLineSelect) {
      onLineSelect(lineNumber);
    }

    // Move cursor to the beginning of the selected line
    if (textareaRef.current) {
      const lines = value.split('\n');
      let charPosition = 0;
      for (let i = 0; i < lineNumber - 1; i++) {
        charPosition += lines[i].length + 1; // +1 for newline character
      }
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(charPosition, charPosition);
    }
  };

  return (
    <div className="flex relative border border-gray-300 rounded-md overflow-y-auto" style={{ maxHeight: '100%' }}>
      {/* Line number gutter */}
      <div
        ref={gutterRef}
        className="w-[40px] bg-gray-100 border-r border-gray-300 flex-shrink-0 font-mono text-sm"
        style={{
          userSelect: 'none',
          lineHeight: '1.5',
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
          height: lineHeights.length > 0
            ? `${lineHeights.reduce((sum, h) => sum + h, 0) + 16}px`
            : '600px',
        }}
      >
        {Array.from({ length: lineCount }, (_, i) => i + 1).map((lineNum, index) => (
          <div
            key={lineNum}
            className={`
              text-gray-500 cursor-pointer hover:bg-gray-200 text-right pr-2 m-0
              ${selectedLine === lineNum ? 'bg-blue-100 !text-blue-700' : ''}
            `}
            style={{
              height: lineHeights[index] ? `${lineHeights[index]}px` : 'auto',
              lineHeight: '1.5',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              paddingTop: '0px',
            }}
            onClick={() => handleLineNumberClick(lineNum)}
          >
            {lineNum}
          </div>
        ))}
      </div>

      {/* Textarea content */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`
          flex-1 font-mono text-sm leading-[1.5] py-2 px-3
          focus:outline-none focus:ring-0
          resize-none overflow-hidden
          ${className}
        `}
        style={{
          minHeight: '600px',
          height: lineHeights.length > 0
            ? `${lineHeights.reduce((sum, h) => sum + h, 0) + 16}px`
            : '600px',
        }}
      />

      {/* Hidden div for measuring line heights */}
      <div
        ref={measureRef}
        className="font-mono text-sm leading-[1.5]"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          left: '-9999px',
          top: '-9999px',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          padding: '0.5rem 0.75rem',
        }}
        aria-hidden="true"
      />
    </div>
  );
};
